/**
 * Investor Portal Auth Service
 *
 * Handles authentication operations for the investor portal
 * Manages dual-token system (access + refresh tokens)
 */

import { investorPortalApiClient } from '../../shared/utils/api-client';
import {
  errorMessage,
  hashEmail,
  newTraceId,
  portalLog,
} from '../../shared/utils/portal-logger';

/**
 * Decoded JWT token payload structure for investor portal
 */
interface TokenPayload {
  sub: string; // contact_id
  session_id: string;
  investor_id?: string;
  exp: number;
  iat: number;
  type: string;
  admin_user_id?: string;
  is_impersonation?: boolean;
}

/**
 * Decode JWT token payload without verifying signature.
 * Safe for client-side use since we only need to read claims.
 * The backend validates signatures on every request.
 */
export function decodeTokenPayload(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    // Base64 decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replaceAll('-', '+').replaceAll('_', '/'));
    return JSON.parse(decoded) as TokenPayload;
  } catch {
    return null;
  }
}

export interface InvestorContextOption {
  contact_id: string;
  investor_id: string;
  investor_name: string;
  investor_type: string;
}

export interface AvailableInvestorsResponse {
  investors: InvestorContextOption[];
  has_fund_carry: boolean;
}

interface SelectInvestorRequest {
  investor_id: string;
}

interface SwitchInvestorRequest {
  contact_id: string;
}

interface MagicLinkRequest {
  email: string;
}

interface MagicLinkResponse {
  message: string;
}

interface DevLoginRequest {
  email: string;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  // The refresh token is delivered via an httponly cookie, not in the body.
}

export class PortalAuthService {
  /**
   * Send magic link to investor email
   * Returns response that may include investor context options if multiple exist
   */
  static async sendMagicLink(email: string): Promise<MagicLinkResponse> {
    const traceId = newTraceId();
    const emailHash = await hashEmail(email);
    portalLog.info('portal.login.magic_link.requested', {
      event: 'magic_link_requested',
      outcome: 'attempt',
      email_hash: emailHash,
      trace_id: traceId,
    });
    try {
      const response = await investorPortalApiClient.post<MagicLinkResponse>(
        '/portal/auth/magic-link',
        { email } as MagicLinkRequest,
        { headers: { 'X-Client-Trace-Id': traceId } }
      );
      portalLog.info('portal.login.magic_link.sent', {
        event: 'magic_link_requested',
        outcome: 'success',
        email_hash: emailHash,
        trace_id: traceId,
      });
      return response;
    } catch (err) {
      portalLog.error('portal.login.magic_link.failed', {
        event: 'magic_link_requested',
        outcome: 'failure',
        email_hash: emailHash,
        trace_id: traceId,
        error: errorMessage(err),
      });
      throw err;
    }
  }

  /**
   * Development login - bypasses magic link for testing
   * Only available in development mode
   */
  static async devLogin(email: string): Promise<void> {
    const response = await investorPortalApiClient.post<AuthResponse>(
      '/portal/auth/dev-login',
      { email } as DevLoginRequest
    );

    // Store the access token; the refresh token is set as an httponly cookie.
    if (response.access_token) {
      investorPortalApiClient.storeTokens(
        response.access_token,
        response.expires_in
      );
    }
  }

  /**
   * Verify magic link token and authenticate investor
   */
  static async verifyMagicLink(token: string): Promise<void> {
    const traceId = newTraceId();
    portalLog.info('portal.login.verify_token.attempt', {
      event: 'verify_token',
      outcome: 'attempt',
      trace_id: traceId,
    });
    try {
      const response = await investorPortalApiClient.post<AuthResponse>(
        '/portal/auth/verify',
        { token },
        { headers: { 'X-Client-Trace-Id': traceId } }
      );

      // Store the access token; the refresh token is set as an httponly cookie.
      if (response.access_token) {
        investorPortalApiClient.storeTokens(
          response.access_token,
          response.expires_in
        );
      }

      const claims = decodeTokenPayload(response.access_token);
      portalLog.info('portal.login.verify_token.success', {
        event: 'verify_token',
        outcome: 'success',
        trace_id: traceId,
        contact_id: claims?.sub,
        session_id: claims?.session_id,
        investor_id: claims?.investor_id,
      });
    } catch (err) {
      portalLog.error('portal.login.verify_token.failed', {
        event: 'verify_token',
        outcome: 'failure',
        trace_id: traceId,
        error: errorMessage(err),
      });
      throw err;
    }
  }

  /**
   * Verify OTP code and authenticate investor (fallback for magic link)
   */
  static async verifyOTP(email: string, code: string): Promise<void> {
    const traceId = newTraceId();
    const emailHash = await hashEmail(email);
    portalLog.info('portal.login.verify_otp.attempt', {
      event: 'verify_otp',
      outcome: 'attempt',
      email_hash: emailHash,
      trace_id: traceId,
    });
    try {
      const response = await investorPortalApiClient.post<AuthResponse>(
        '/portal/auth/verify-otp',
        { email, code },
        { headers: { 'X-Client-Trace-Id': traceId } }
      );

      // Store the access token; the refresh token is set as an httponly cookie.
      if (response.access_token) {
        investorPortalApiClient.storeTokens(
          response.access_token,
          response.expires_in
        );
      }

      const claims = decodeTokenPayload(response.access_token);
      portalLog.info('portal.login.verify_otp.success', {
        event: 'verify_otp',
        outcome: 'success',
        email_hash: emailHash,
        trace_id: traceId,
        contact_id: claims?.sub,
        session_id: claims?.session_id,
        investor_id: claims?.investor_id,
      });
    } catch (err) {
      portalLog.error('portal.login.verify_otp.failed', {
        event: 'verify_otp',
        outcome: 'failure',
        email_hash: emailHash,
        trace_id: traceId,
        error: errorMessage(err),
      });
      throw err;
    }
  }

  /**
   * Logout - clear tokens and optionally call backend to revoke session
   */
  static async logout(): Promise<void> {
    try {
      // Call backend to revoke session (best effort - don't throw on failure)
      const token = localStorage.getItem('investor_portal_token');
      if (token) {
        await investorPortalApiClient.post('/portal/auth/logout', {});
      }
    } catch (error) {
      // Ignore logout errors - we'll clear tokens anyway
      console.warn('Logout request failed:', error);
    } finally {
      // Always clear local tokens
      investorPortalApiClient.clearTokens();
    }
  }

  /**
   * Check if investor is authenticated (has valid or refreshable tokens)
   * The refresh token lives in an httponly cookie and is not readable here.
   * Presence of an access token indicates an active session; the API client transparently refreshes it via the cookie when it expires.
   */
  static isAuthenticated(): boolean {
    return !!localStorage.getItem('investor_portal_token');
  }

  /**
   * Get current access token
   */
  static getToken(): string | null {
    return localStorage.getItem('investor_portal_token');
  }

  /**
   * Check if access token is expired or will expire soon
   */
  static isTokenExpiringSoon(): boolean {
    const expiresAt = localStorage.getItem('investor_portal_token_expires_at');
    if (!expiresAt) return true;

    const expirationTime = Number.parseInt(expiresAt, 10);
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    return Date.now() >= expirationTime - bufferTime;
  }

  /**
   * Get the investor contexts the logged-in identity can act as, plus whether
   * it also has carry. A carry-only identity gets an empty investor list.
   */
  static async getAvailableInvestors(): Promise<AvailableInvestorsResponse> {
    return investorPortalApiClient.get<AvailableInvestorsResponse>(
      '/portal/auth/available-investors'
    );
  }

  /**
   * Select an investor after authentication
   * Used in new login flow where user selects investor after login
   */
  static async selectInvestor(investorId: string): Promise<void> {
    const response = await investorPortalApiClient.post<AuthResponse>(
      '/portal/auth/select-investor',
      { investor_id: investorId } as SelectInvestorRequest
    );

    // Store the access token; the refresh token is set as an httponly cookie.
    if (response.access_token) {
      investorPortalApiClient.storeTokens(
        response.access_token,
        response.expires_in
      );
    }
  }

  /**
   * Switch to a different investor context
   */
  static async switchInvestor(contactId: string): Promise<void> {
    const response = await investorPortalApiClient.post<AuthResponse>(
      '/portal/auth/switch-investor',
      { contact_id: contactId } as SwitchInvestorRequest
    );

    // Store the access token; the refresh token is set as an httponly cookie.
    if (response.access_token) {
      investorPortalApiClient.storeTokens(
        response.access_token,
        response.expires_in
      );
    }
  }
}
